/**
 * Product-card preview media: play audio/video on the card image.
 * Only one card plays at a time.
 *
 * @extends {HTMLElement}
 */
class ProductCardMediaPlay extends HTMLElement {
  /** @type {ProductCardMediaPlay | null} */
  static activeInstance = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const button = this.querySelector('.product-card-media-play__button');
    const media = this.querySelector('.product-card-media-play__media');

    if (!(button instanceof HTMLButtonElement) || !(media instanceof HTMLMediaElement)) return;

    button.addEventListener(
      'click',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
      },
      { signal }
    );

    media.addEventListener(
      'ended',
      () => {
        this.stop();
      },
      { signal }
    );

  }

  disconnectedCallback() {
    if (ProductCardMediaPlay.activeInstance === this) {
      ProductCardMediaPlay.activeInstance = null;
    }
    this.#abortController?.abort();
  }

  toggle() {
    const media = this.querySelector('.product-card-media-play__media');
    if (!(media instanceof HTMLMediaElement)) return;

    if (this.hasAttribute('data-playing') && !media.paused) {
      this.stop();
      return;
    }

    this.play();
  }

  play() {
    const media = this.querySelector('.product-card-media-play__media');
    const button = this.querySelector('.product-card-media-play__button');
    if (!(media instanceof HTMLMediaElement) || !(button instanceof HTMLButtonElement)) return;

    if (ProductCardMediaPlay.activeInstance && ProductCardMediaPlay.activeInstance !== this) {
      ProductCardMediaPlay.activeInstance.stop();
    }

    ProductCardMediaPlay.activeInstance = this;
    this.#setPlaying(true);

    const playPromise = media.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        this.stop();
      });
    }
  }

  stop() {
    const media = this.querySelector('.product-card-media-play__media');
    if (media instanceof HTMLMediaElement) {
      media.pause();
      media.currentTime = 0;
    }

    this.#setPlaying(false);

    if (ProductCardMediaPlay.activeInstance === this) {
      ProductCardMediaPlay.activeInstance = null;
    }
  }

  /**
   * @param {boolean} playing
   */
  #setPlaying(playing) {
    const button = this.querySelector('.product-card-media-play__button');
    if (!(button instanceof HTMLButtonElement)) return;

    if (playing) {
      this.setAttribute('data-playing', 'true');
      button.setAttribute('aria-label', button.dataset.pauseLabel || 'Pause');
    } else {
      this.removeAttribute('data-playing');
      button.setAttribute('aria-label', button.dataset.playLabel || 'Play');
    }
  }
}

if (!customElements.get('product-card-media-play')) {
  customElements.define('product-card-media-play', ProductCardMediaPlay);
}
